<script setup lang="ts">
import { useId } from 'vue'
import { useTranslation } from 'i18next-vue'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import { SELECTABLE_ENTRY_FUNCTIONS } from '@/types/formatInstitution.ts'

const visible = defineModel<boolean>('visible', { required: true })

const { t } = useTranslation()

const descriptionId = `${useId()}-description`

// Own org can additionally hold ORGANIZED_AND_CONDUCTED_BY.
const functions = [...SELECTABLE_ENTRY_FUNCTIONS, 'ORGANIZED_AND_CONDUCTED_BY'] as const
</script>

<template>
    <Dialog
        v-model:visible="visible"
        modal
        :closable="true"
        class="w-modal-md"
        :header="t('ui.institutions.functionHelp.title')"
        :aria-describedby="descriptionId"
    >
        <div class="space-y-6">
            <p :id="descriptionId" class="paragraph-sm text-neutral-600">
                {{ t('ui.institutions.functionHelp.description') }}
            </p>

            <dl class="space-y-6 bg-blue-50 rounded-xl p-6">
                <div v-for="functionValue in functions" :key="functionValue">
                    <dt class="font-semibold">
                        {{ t(`ui.institutions.function.${functionValue}.label`) }}
                    </dt>
                    <dd class="paragraph-sm text-neutral-600">
                        {{ t(`ui.institutions.function.${functionValue}.description`) }}
                    </dd>
                    <dd
                        v-if="
                            t(`ui.institutions.function.${functionValue}.example`, {
                                defaultValue: '',
                            })
                        "
                        class="paragraph-sm text-neutral-600 italic"
                    >
                        {{ t(`ui.institutions.function.${functionValue}.example`) }}
                    </dd>
                </div>
            </dl>
        </div>

        <template #footer>
            <div class="flex justify-end">
                <Button
                    :label="t('ui.actions.close')"
                    data-testid="institution-function-help-close-button"
                    @click="visible = false"
                />
            </div>
        </template>
    </Dialog>
</template>
